import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, Field, Flex, Input, Message, Modal, Text } from '@workwhile/ui'

import { useVndlyPositionIssuesContextValue } from '../context/VndlyPositionIssuesProvider'
import { useVndlyIssuesContextValue } from 'pages/VndlyIssues/context/VndlyIssuesProvider'

export const linkVndlyPositionFormSchema = z.object({
  vndlyJobTitle: z.string().trim().min(1, 'VNDLY job title is required'),
})

export type LinkVndlyPositionForm = z.infer<typeof linkVndlyPositionFormSchema>

type LinkVndlyPositionButtonProps = {
  companyPositionId: number
  positionName: string
  companyId: number
  vndlyInstanceId: number
}

export const LinkVndlyPositionButton = ({
  companyPositionId,
  positionName,
  companyId,
  vndlyInstanceId,
}: LinkVndlyPositionButtonProps) => {
  const {
    actions: { linkVndlyPosition, resetLinkVndlyPosition },
    state: { linkVndlyPositionMutationState },
  } = useVndlyPositionIssuesContextValue()

  const {
    state: { instances },
  } = useVndlyIssuesContextValue()

  const instanceUrl =
    instances.find((instance) => instance.id === vndlyInstanceId)
      ?.instanceUrl ?? vndlyInstanceId.toString()

  const companyName =
    instances
      .find((instance) => instance.id === vndlyInstanceId)
      ?.companies.find((company) => company.id === companyId)?.name ??
    companyId.toString()

  const [isOpen, setIsOpen] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LinkVndlyPositionForm>({
    resolver: zodResolver(linkVndlyPositionFormSchema),
    defaultValues: {
      vndlyJobTitle: '',
    },
  })

  useEffect(() => {
    if (!linkVndlyPositionMutationState.isSuccess) {
      return
    }

    setIsOpen(false)
    reset()
    resetLinkVndlyPosition()
  }, [linkVndlyPositionMutationState.isSuccess, reset, resetLinkVndlyPosition])

  const handleClick = () => {
    resetLinkVndlyPosition()
    reset()
    setIsOpen(true)
  }

  const handleCancel = () => {
    resetLinkVndlyPosition()
    reset()
    setIsOpen(false)
  }

  const onSubmit = handleSubmit((data) => {
    linkVndlyPosition({
      vndlyInstanceId,
      companyPositionId,
      vndlyJobTitle: data.vndlyJobTitle,
    })
  })

  return (
    <>
      <Button
        variant="secondary"
        kind="small"
        onClick={handleClick}
        style={{ padding: '0 0.5rem' }}
        disabled={linkVndlyPositionMutationState.isPending}
        loading={linkVndlyPositionMutationState.isPending}
      >
        <Text fontSize="12px">Link position</Text>
      </Button>
      {isOpen ? (
        <Modal
          open
          onClose={handleCancel}
          title={`Link position for ${positionName}`}
        >
          <Text mb={2}>
            Enter the VNDLY job title to link {positionName} (
            {companyPositionId}) for {companyName} ({companyId}) on instance{' '}
            {instanceUrl}.
          </Text>
          {linkVndlyPositionMutationState.isError &&
          linkVndlyPositionMutationState.errorMessage ? (
            <Message variant="error" mb={3}>
              {linkVndlyPositionMutationState.errorMessage}
            </Message>
          ) : null}
          <form onSubmit={onSubmit}>
            <Field
              label="VNDLY job title"
              error={errors.vndlyJobTitle?.message}
            >
              <Input {...register('vndlyJobTitle')} />
            </Field>
            <Flex flexDirection="row" justifyContent="space-between" gap={2}>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={linkVndlyPositionMutationState.isPending}
                disabled={linkVndlyPositionMutationState.isPending}
              >
                Submit
              </Button>
            </Flex>
          </form>
        </Modal>
      ) : null}
    </>
  )
}
